import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/currency';
import VendorModal from './VendorModal';

const PurchaseOrderForm = ({ onClose, onSuccess, editOrder = null }) => {
  const [formData, setFormData] = useState({
    vendorId: '',
    requisitionId: '',
    notes: '',
    status: 'DRAFT',
    termsConditions: '',
    vatPercentage: 5
  });
  const [vendors, setVendors] = useState([]);
  const [parts, setParts] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [items, setItems] = useState([{ partId: '', quantity: 1, unitPrice: 0, description: '', discount: 0 }]);
  const [loading, setLoading] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [showVendorModal, setShowVendorModal] = useState(false);

  useEffect(() => {
    fetchData();
    if (editOrder) {
      populateForm();
    } else {
      // Check for pre-selected parts from Parts Management
      const selectedPartsData = localStorage.getItem('selectedPartsForPO');
      if (selectedPartsData) {
        try {
          const selectedParts = JSON.parse(selectedPartsData);
          if (selectedParts && selectedParts.length > 0) {
            const preSelectedItems = selectedParts.map(part => ({
              partId: part.id.toString(),
              quantity: 1,
              unitPrice: parseFloat(part.price) || parseFloat(part.costPrice) || 0,
              description: '',
              discount: 0,
              partName: part.name
            }));
            setItems(preSelectedItems);
            // Clear the localStorage after using it
            localStorage.removeItem('selectedPartsForPO');
            toast.success(`${selectedParts.length} parts pre-loaded from selection`);
          }
        } catch (error) {
          console.error('Error parsing selected parts:', error);
          localStorage.removeItem('selectedPartsForPO');
        }
      }
    }
  }, [editOrder]);

  const fetchData = async () => {
    try {
      const [vendorsRes, partsRes, orgRes, reqRes] = await Promise.all([
        axios.get('/api/vendors'),
        axios.get('/api/parts'),
        axios.get('/api/organization'),
        axios.get('/api/requisitions?status=APPROVED')
      ]);
      setVendors(vendorsRes.data.vendors || vendorsRes.data || []);
      setParts(partsRes.data.parts || partsRes.data || []);
      setOrganization(orgRes.data);
      setRequisitions(reqRes.data.requisitions || reqRes.data || []);
      
      if (orgRes.data?.vatPercentage) {
        setFormData(prev => ({ ...prev, vatPercentage: orgRes.data.vatPercentage }));
      }
      
      // Set standard PO terms & conditions
      const standardPOTerms = 
        `• Payment Terms: Net 30 days from delivery
• All items must be delivered as per specifications and quality standards
• Quality inspection will be conducted upon delivery
• Any discrepancies must be reported within 48 hours of delivery
• Prices are inclusive of VAT where applicable
• Delivery should be made to warehouse during business hours
• Supplier must provide delivery receipt and invoice
• Returns accepted only for defective items within 7 days`;
      
      if (!editOrder) {
        setFormData(prev => ({ ...prev, termsConditions: standardPOTerms }));
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    }
  };

  const populateForm = () => {
    const standardPOTerms = 
      `• Payment Terms: Net 30 days from delivery
• All items must be delivered as per specifications and quality standards
• Quality inspection will be conducted upon delivery
• Any discrepancies must be reported within 48 hours of delivery
• Prices are inclusive of VAT where applicable
• Delivery should be made to warehouse during business hours
• Supplier must provide delivery receipt and invoice
• Returns accepted only for defective items within 7 days`;
    
    setFormData({
      vendorId: editOrder.vendorId || '',
      requisitionId: editOrder.requisitionId || '',
      notes: editOrder.notes || '',
      status: editOrder.status || 'DRAFT',
      termsConditions: editOrder.termsConditions || standardPOTerms,
      vatPercentage: editOrder.vatPercentage || 5
    });
    setItems(editOrder.items || [{ partId: '', quantity: 1, unitPrice: 0, description: '', discount: 0 }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const subtotal = calculateSubtotal();
      const vatAmount = calculateVAT();
      const totalAmount = calculateTotal();
      
      const submitData = {
        ...formData,
        items: items.filter(item => item.partId && item.quantity > 0),
        subtotal,
        vatAmount,
        totalAmount,
        requisitionId: formData.requisitionId ? parseInt(formData.requisitionId) : null
      };

      if (editOrder) {
        await axios.put(`/api/purchase-orders/${editOrder.id}`, submitData);
        toast.success('Purchase order updated successfully');
      } else {
        await axios.post('/api/purchase-orders', submitData);
        toast.success('Purchase order created successfully');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save purchase order');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setItems([...items, { partId: '', quantity: 1, unitPrice: 0, description: '', discount: 0 }]);
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    
    if (field === 'partId') {
      const part = parts.find(p => p.id === parseInt(value));
      if (part) {
        updated[index].unitPrice = parseFloat(part.price) || 0;
        updated[index].partName = part.name;
      }
    }
    
    setItems(updated);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => {
      const itemTotal = item.quantity * item.unitPrice;
      const discountAmount = itemTotal * ((item.discount || 0) / 100);
      return sum + (itemTotal - discountAmount);
    }, 0);
  };

  const calculateVAT = () => {
    return (calculateSubtotal() * formData.vatPercentage) / 100;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateVAT();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">
            {editOrder ? 'Edit Purchase Order' : 'Create Purchase Order'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Requisition</label>
              {requisitions.length === 0 && (
                <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                  <strong>No approved requisitions available.</strong> Only APPROVED requisitions appear here. 
                  Go to Purchase Requisitions to approve pending requests first.
                </div>
              )}
              <select
                value={formData.requisitionId}
                onChange={(e) => {
                  const reqId = e.target.value;
                  setFormData({ ...formData, requisitionId: reqId });
                  
                  // Auto-populate items from selected requisition
                  if (reqId) {
                    const selectedReq = requisitions.find(r => r.id === parseInt(reqId));
                    if (selectedReq && selectedReq.items) {
                      setItems(selectedReq.items.map(item => ({
                        partId: item.partId,
                        quantity: item.quantity,
                        unitPrice: item.unitPrice || item.estimatedPrice || 0,
                        partName: item.partName,
                        description: item.description || '',
                        discount: item.discount || 0
                      })));
                    }
                  }
                }}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Requisition (Optional)</option>
                {requisitions.map(req => (
                  <option key={`req-${req.id}`} value={req.id}>
                    {req.requestNo} - {req.requester?.name} ({formatCurrency(req.totalAmount || 0, organization?.currency_code)})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor *</label>
              <div className="flex space-x-2">
                <select
                  value={formData.vendorId}
                  onChange={(e) => setFormData({ ...formData, vendorId: e.target.value })}
                  required
                  className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Vendor</option>
                  {vendors.map(vendor => (
                    <option key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowVendorModal(true)}
                  className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                  title="Create New Vendor"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="DRAFT">Draft</option>
                <option value="SENT">Sent</option>
                <option value="CONFIRMED">Confirmed</option>
                <option value="PARTIAL_RECEIVED">Partial Received</option>
                <option value="RECEIVED">Received</option>
                <option value="PAID">Paid</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">Items</label>
              <button
                type="button"
                onClick={addItem}
                className="flex items-center px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Item
              </button>
            </div>

            {items.map((item, index) => {
              const selectedPart = parts.find(p => p.id === parseInt(item.partId));
              return (
                <div key={index} className="mb-3 border-b pb-2">
                  <div className="grid grid-cols-12 gap-2 mb-2">
                    <div className="col-span-4">
                      <select
                        value={item.partId}
                        onChange={(e) => updateItem(index, 'partId', e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm"
                      >
                        <option value="">Select Part</option>
                        {parts.map(part => (
                          <option key={part.id} value={part.id}>
                            {part.name} (Stock: {part.quantity})
                          </option>
                        ))}
                      </select>
                      {selectedPart && (
                        <div className="text-xs text-gray-500 mt-1">
                          Current Stock: {selectedPart.quantity} | Cost: {formatCurrency(selectedPart.costPrice || 0, organization?.currency_code)}
                        </div>
                      )}
                    </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1 border rounded text-sm"
                      min="1"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      placeholder="Unit Price"
                      value={item.unitPrice}
                      onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                      step="0.01"
                      className="w-full px-2 py-1 border rounded text-sm"
                    />
                  </div>
                  <div className="col-span-2 relative">
                    <input
                      type="number"
                      placeholder="Discount"
                      value={item.discount}
                      onChange={(e) => updateItem(index, 'discount', parseFloat(e.target.value) || 0)}
                      step="0.01"
                      min="0"
                      max="100"
                      className="w-full px-2 py-1 pr-6 border rounded text-sm"
                    />
                    <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">%</span>
                  </div>
                  <div className="col-span-1">
                    <span className="text-sm">{formatCurrency(item.quantity * item.unitPrice * (1 - (item.discount || 0) / 100), organization?.currency_code)}</span>
                  </div>
                  <div className="col-span-1">
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-11">
                    <input
                      type="text"
                      placeholder="Additional description (optional)"
                      value={item.description || ''}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                      className="w-full px-2 py-1 border rounded text-sm"
                    />
                  </div>
                </div>
              </div>
            );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">VAT Percentage (%)</label>
              <input
                type="number"
                value={formData.vatPercentage}
                onChange={(e) => setFormData({ ...formData, vatPercentage: parseFloat(e.target.value) || 0 })}
                step="0.1"
                min="0"
                max="100"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
            <textarea
              value={formData.termsConditions}
              onChange={(e) => setFormData({ ...formData, termsConditions: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter terms and conditions for this purchase order..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <div className="flex justify-between text-sm">
              <span>Items Count:</span>
              <span>{items.filter(item => item.partId && item.quantity > 0).length}</span>
            </div>
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>{formatCurrency(calculateSubtotal(), organization?.currency_code)}</span>
            </div>
            <div className="flex justify-between">
              <span>VAT ({formData.vatPercentage}%):</span>
              <span>{formatCurrency(calculateVAT(), organization?.currency_code)}</span>
            </div>
            <div className="flex justify-between items-center border-t pt-2">
              <span className="text-lg font-semibold">Total Amount:</span>
              <span className="text-lg font-bold">{formatCurrency(calculateTotal(), organization?.currency_code)}</span>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : editOrder ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
      
      {/* Vendor Modal */}
      {showVendorModal && (
        <VendorModal
          onSuccess={(newVendor) => {
            setVendors(prev => [...prev, newVendor]);
            setFormData(prev => ({ ...prev, vendorId: newVendor.id }));
            setShowVendorModal(false);
          }}
          onClose={() => setShowVendorModal(false)}
        />
      )}
    </div>
  );
};

export default PurchaseOrderForm;